import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  CircularProgress,
  Table,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
} from '@mui/material';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import { isAxiosError } from 'axios';
import moment from 'moment';

import { useLoggedinUser } from 'features/auth/hooks/queries/authQueries';
import { useOptionalFilterValues } from 'hooks/useFilterContext';
import StoreTableModel from 'models/store/StoreTableModel';
import { StoreTableProps } from 'types';
import { getStoreReconciliationInvestigation, StoreInvestigationReport } from '../services/StoresService';
import { sortStoreRows, StoreSortField, StoreSortState } from '../utils/sorting/storeTableSorting';
import StoreInvestigationDialog, { StoreInvestigationTarget } from './StoreInvestigationDialog';
import StoreTableBody from './StoreTableBody';

const StoreTable: React.FC<StoreTableProps> = ({ data, isLoading }) => {
  const [sortState, setSortState] = useState<StoreSortState>({ field: 'store', direction: 'asc' });
  const [investigationTarget, setInvestigationTarget] = useState<StoreInvestigationTarget | null>(null);
  const [investigationReport, setInvestigationReport] = useState<StoreInvestigationReport | null>(null);
  const [investigationError, setInvestigationError] = useState<string | null>(null);
  const [isInvestigating, setIsInvestigating] = useState(false);
  const investigationControllerRef = useRef<AbortController | null>(null);
  const { startDate, endDate } = useOptionalFilterValues();
  const { data: loggedInUser } = useLoggedinUser();
  const businessDate = startDate && endDate && moment(startDate).isSame(moment(endDate), 'day')
    ? moment(startDate).format('YYYY-MM-DD')
    : null;
  const sortedData = useMemo(() => sortStoreRows(data || [], sortState), [data, sortState]);
  const storeCount = sortedData.length;
  useEffect(() => () => investigationControllerRef.current?.abort(), []);

  const handleInvestigate = async (row: StoreTableProps['data'][number]): Promise<void> => {
    if (!businessDate) return;

    investigationControllerRef.current?.abort();
    const controller = new AbortController();
    investigationControllerRef.current = controller;
    const storeModel = StoreTableModel.fromServerData(row);
    setInvestigationTarget({
      storeName: storeModel.name,
      businessDate,
      auditQuantity: storeModel.totalSold,
      posQuantity: storeModel.posScratcherCount,
      quantityVariance: storeModel.quantityVariance ?? 0,
      auditSales: storeModel.revenue,
      posSales: storeModel.posScratcherSales,
      salesVariance: storeModel.revenueVariance ?? 0,
    });
    setInvestigationReport(null);
    setInvestigationError(null);
    setIsInvestigating(true);

    try {
      const report = await getStoreReconciliationInvestigation(storeModel.id, businessDate, controller.signal);
      if (investigationControllerRef.current === controller) {
        setInvestigationReport(report);
      }
    } catch (error: unknown) {
      if (isAxiosError(error) && error.code === 'ERR_CANCELED') return;
      if (investigationControllerRef.current !== controller) return;

      const detail = isAxiosError(error) ? error.response?.data?.detail : undefined;
      const message = typeof detail === 'string'
        ? detail
        : detail && typeof detail === 'object' && 'message' in detail && typeof detail.message === 'string'
          ? detail.message
          : error instanceof Error
            ? error.message
            : 'Unable to run the scratcher investigation.';
      setInvestigationError(message);
    } finally {
      if (investigationControllerRef.current === controller) {
        investigationControllerRef.current = null;
        setIsInvestigating(false);
      }
    }
  };

  const handleCloseInvestigation = (): void => {
    investigationControllerRef.current?.abort();
    investigationControllerRef.current = null;
    setInvestigationTarget(null);
    setInvestigationReport(null);
    setInvestigationError(null);
    setIsInvestigating(false);
  };

  const handleSort = (field: StoreSortField): void => {
    setSortState((previous) => ({
      field,
      direction: previous.field === field && previous.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const renderSortLabel = (label: React.ReactNode, field: StoreSortField): React.ReactNode => {
    const isActive = sortState.field === field;
    const direction = isActive ? sortState.direction : 'asc';
    const SortIcon = direction === 'asc' ? ArrowUpwardIcon : ArrowDownwardIcon;

    return (
      <TableSortLabel
        active={isActive}
        direction={direction}
        onClick={() => handleSort(field)}
        hideSortIcon={false}
        IconComponent={SortIcon}
        sx={{
          width: '100%',
          justifyContent: 'flex-start',
          '& .MuiTableSortLabel-icon': { opacity: 1 },
          '& .MuiTableSortLabel-iconDirectionAsc, & .MuiTableSortLabel-iconDirectionDesc': {
            transform: 'none !important',
          },
        }}
      >
        {label}
      </TableSortLabel>
    );
  };

  return (
    <>
      <TableContainer className="table-container--store store-comparison-table">
      <Table size="small" stickyHeader aria-label="Store reconciliation comparison">
        <TableHead>
          <TableRow>
            <TableCell>
              {renderSortLabel(
                <span className="table-header-text store-comparison-table__store-heading">
                  Stores
                  {isLoading ? <CircularProgress size={12} color="inherit" /> : <span>({storeCount})</span>}
                </span>,
                'store'
              )}
            </TableCell>
            <TableCell aria-label="Metric" />
            <TableCell align="right"><span className="table-header-text">Expected</span></TableCell>
            <TableCell align="right"><span className="table-header-text">POS Reported</span></TableCell>
            <TableCell align="right">
              {renderSortLabel(<span className="table-header-text">Variance</span>, 'finalVariance')}
            </TableCell>
          </TableRow>
        </TableHead>

        <StoreTableBody
          data={sortedData}
          isLoading={isLoading}
          isSingleDay={Boolean(businessDate)}
          isSuperAdmin={Boolean(loggedInUser?.is_super_admin)}
          onInvestigate={handleInvestigate}
        />

        </Table>
      </TableContainer>
      <StoreInvestigationDialog
        open={Boolean(investigationTarget)}
        target={investigationTarget}
        report={investigationReport}
        isLoading={isInvestigating}
        error={investigationError}
        onClose={handleCloseInvestigation}
      />
    </>
  );
};

export default StoreTable;
